/**
 * Enhanced ProductCard Component with Cart Integration - BRAND STYLED VERSION
 * Shows current cart quantities in quantity inputs and updates cart directly
 * Performance optimized to prevent unnecessary re-renders
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { formatCurrency } from "../../utils/formatters";
import { useCart } from "../../hooks/useCart";
import { useBackOrder } from "../../hooks/useBackOrder";
import { useAuth } from "../../hooks/useAuth";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import { isBelowOneCase } from "../../utils/quantityHelpers";
import QuantityInput from "../cart/QuantityInput";
import { showProductModal } from "../../utils/modalHelpers";
import ImageComingSoon from "../common/ImageComingSoon";
import { PricingBadge } from "../common/PricingTierTable";
import { useMedRecFilter } from "../../context/MedRecFilterContext";
import BackOrderAdjustModal from "../backorder/BackOrderAdjustModal";

// Cannabis icon (used for both med and rec badges)
import cannabisIcon from "../../assets/icons/reccannabis.png";

const ProductCard = React.memo(
  ({ product, showAddToCart = true }) => {
    const { isEmployee } = useAuth();
    const effectiveShowAddToCart = showAddToCart && !isEmployee();
    const [imageError, setImageError] = useState(false);
    const [showQuantitySelector, setShowQuantitySelector] = useState(false);

    const {
      cartData,
      updateProductQuantity,
      isUpdatingProductQuantity,
    } = useCart();

    const { getItemBackOrderQuantity, canUseBackOrder } = useBackOrder();

    // Back-order modal (create / view / adjust the back-order quantity for this product)
    const [showBackOrderModal, setShowBackOrderModal] = useState(false);

    // Med/Rec filter context - only show badge when store supports both
    const { showFilter: showMedRecBadge } = useMedRecFilter();

    // Create quantity calculator
    const calculator = useMemo(() => {
      return product ? new ProductQuantityCalculator(product) : null;
    }, [product?.id, product?.unitsPerCase, product?.optSellByTypeID]);

    // Get current quantity from cart with stable dependency
    const currentCartQuantity = useMemo(() => {
      if (!product || !cartData?.draftCartOrderItems) return 0;
      const item = cartData.draftCartOrderItems.find(
        (item) => item.productSkuVariantID === product.id,
      );
      return item?.unitQuantity || 0;
    }, [product?.id, cartData?.draftCartOrderItems]);

    // Convert cart quantity to cases/units display
    const currentDisplay = useMemo(() => {
      if (!calculator) return { cases: 0, units: 0 };
      return calculator.unitsToDisplay(currentCartQuantity);
    }, [calculator, currentCartQuantity]);

    // Local state for quantity inputs (initialized from cart)
    const [caseQuantity, setCaseQuantity] = useState(currentDisplay.cases);
    const [singleUnitQuantity, setSingleUnitQuantity] = useState(
      currentDisplay.units,
    );

    // Update local state when cart changes
    useEffect(() => {
      setCaseQuantity(currentDisplay.cases);
      setSingleUnitQuantity(currentDisplay.units);
    }, [currentDisplay.cases, currentDisplay.units]);

    // Validate quantities
    const validation = useMemo(() => {
      if (!calculator) return { isValid: true, error: null };
      return calculator.validateQuantity(caseQuantity, singleUnitQuantity);
    }, [calculator, caseQuantity, singleUnitQuantity]);

    // Calculate pricing
    const priceInfo = useMemo(() => {
      if (!calculator) return null;
      const totalUnits = calculator.displayToUnits(caseQuantity, singleUnitQuantity);
      return calculator.calculatePrice(totalUnits);
    }, [calculator, caseQuantity, singleUnitQuantity]);

    // Handle quantity updates with debouncing
    const handleQuantityUpdate = async (newCases, newUnits) => {
      if (!calculator || !product) return;

      const newTotal = calculator.displayToUnits(newCases, newUnits);

      try {
        await updateProductQuantity(product.id, newTotal, {
          unitPrice: (product.discountUnitPrice && product.discountUnitPrice > 0) ? product.discountUnitPrice : product.unitPrice,
          singleUnitFee: product.singleUnitFee,
          productSkuVariant: product,
        });
      } catch (error) {
        console.error("Failed to update product quantity:", error);
        // Revert to cart values on error
        setCaseQuantity(currentDisplay.cases);
        setSingleUnitQuantity(currentDisplay.units);
      }
    };

    // Handle case quantity change
    const handleCaseChange = (newCases) => {
      setCaseQuantity(newCases);
      handleQuantityUpdate(newCases, singleUnitQuantity);
    };

    // Handle unit quantity change
    const handleUnitChange = (newUnits) => {
      setSingleUnitQuantity(newUnits);
      handleQuantityUpdate(caseQuantity, newUnits);
    };

    // ---- Back order affordance ----
    // Only offer a back order once less than a full case is left to buy (available minus cart).
    const canCreateBackOrder =
      effectiveShowAddToCart && product?.allowBackorder && isBelowOneCase(product, currentCartQuantity);

    const currentBackOrderQty = getItemBackOrderQuantity(product?.id);

    const handleImageError = () => {
      setImageError(true);
    };

    const handleQuickAdd = () => {
      setShowQuantitySelector(true);
      if (currentCartQuantity === 0) {
        if (calculator?.canSellByCase && product.unitAvailable >= product.unitsPerCase) {
          // Enough stock for a full case and product sells by case
          handleCaseChange(1);
        } else if (calculator?.canSellByUnit) {
          // Sell by unit: either unit-only product or not enough stock for a case
          handleUnitChange(1);
        } else {
          // Case-only product without enough stock for a full case — just show selector
        }
      }
    };

    // Check if we have a valid image
    const hasValidImage = () => {
      return !imageError && product.imageUrl && product.imageUrl !== "/placeholder-product.jpg";
    };

    // Get the effective unit price based on current cart quantity (MOQ-aware)
    const effectiveUnitPrice = useMemo(() => {
      if (!calculator) return product.unitPrice;
      // If items in cart, use resolved price based on quantity
      if (currentCartQuantity > 0) {
        const resolved = calculator.resolveUnitPrice(currentCartQuantity);
        return resolved.price;
      }
      // Otherwise show base price (discountUnitPrice or unitPrice)
      return calculator.getBaseUnitPrice();
    }, [calculator, currentCartQuantity, product.unitPrice]);

    // Check if currently getting MOQ pricing
    const isGettingMOQPrice = useMemo(() => {
      if (!calculator || currentCartQuantity === 0) return false;
      const resolved = calculator.resolveUnitPrice(currentCartQuantity);
      return resolved.isMOQ;
    }, [calculator, currentCartQuantity]);

    // Format price display - now using MOQ-aware pricing
    const getCasePriceDisplay = () => {
      const casePrice = effectiveUnitPrice * product.unitsPerCase;
      return formatCurrency(casePrice);
    };

    const getOriginalCasePriceDisplay = () => {
      const casePrice = product.unitPrice * product.unitsPerCase;
      return formatCurrency(casePrice);
    };

    const getUnitPriceDisplay = () => {
      return formatCurrency(effectiveUnitPrice);
    };

    const getOriginalUnitPriceDisplay = () => {
      return formatCurrency(product.unitPrice);
    };

    // Check for discount/volume pricing - show strikethrough if any savings
    const hasDiscount = calculator?.hasDiscountPrice() || isGettingMOQPrice;
    const hasMOQPricing = calculator?.hasMOQPricing();

    // Calculate max quantities based on availability
    const getMaxCases = () => {
      return Math.floor(product.unitAvailable / product.unitsPerCase);
    };

    const getMaxUnits = () => {
      const remainingAfterCases =
        product.unitAvailable - caseQuantity * product.unitsPerCase;
      return Math.min(product.unitsPerCase - 1, remainingAfterCases);
    };

    return (
      <div className="stash-product-card border-1 border-dark">
        {/* Brand badge */}
          {product.brandName && (
            <div className="text-white bg-stash-primary-dark text-center py-1">
              {product.brandName}
            </div>
          )}
        <div className="stash-product-card__body">
          {/* Badges positioned above the card */}
          <div className="stash-product-card__badges mt-1 justify-content-end pe-1 position-absolute end-0" style={{zIndex:1}}>

            {/* Med/Rec badge - only show when store supports both types */}
            {showMedRecBadge && (product.isMed || product.isRec) && (
              <span
                className={`badge rounded-pill d-inline-flex align-items-center ${
                  product.isMed ? 'bg-success' : 'bg-purple'
                }`}
                title={product.isMed ? 'Medical Product' : 'Recreational Product'}
              >
                <img src={cannabisIcon} alt="" className="med-rec-badge-icon me-1" />
                {product.isMed ? 'Med' : 'Rec'}
              </span>
            )}

            {/* Stock status - only show quantity when low stock (<100) or out of stock */}
            {(!product.isInStock || product.unitAvailable < 100) && (
              <span
                className={`badge rounded-pill ${product.isInStock ? 'bg-stash-primary' : 'bg-stash-gray-800'}`}
              >
                {product.isInStock
                  ? `${product.unitAvailable} available`
                  : "Coming Soon"}
              </span>
            )}

            {/* Cart quantity indicator - shows if items in cart */}
            {currentCartQuantity > 0 && (
              <span className="badge rounded-pill bg-dark">
                <i className="bi bi-cart-fill me-1"></i>
                {calculator?.getQuantityDisplayText(currentCartQuantity)}
              </span>
            )}
                        

            {/* Image count indicator */}
            {product.hasImages && product.images && product.images.length > 1 && (
              <span className="badge rounded-pill bg-secondary">
                <i className="bi bi-images me-1"></i>
                {product.images.length}
              </span>
            )}
          </div>

          <div className="stash-product-card__image-container">
            <Link to={`/products/${product.id}`} className="stash-product-card__link">
              {hasValidImage() ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className={`stash-product-card__image ${!product.isInStock && 'opacity-50'} `}
                  loading="lazy"
                  onError={handleImageError}
                />
              ) : (
                <ImageComingSoon className="stash-product-card__image" />
              )}
            </Link>
          </div>

          <div className="stash-product-card__content">
            <h3 className="stash-product-card__title text-center">
              <Link
                to={`/products/${product.id}`}
                className="stash-product-card__title-link"
              >
                {product.name}
                <div className="stash-product-card__flavor">
                {product.flavor ? (
                  <>
                  {product.flavor}
                  </>
                ):(
                   <>
                  {" "}
                  </>
                )}
                </div>
               
              </Link>
            </h3>
            <hr className="my-0"></hr>
            <div className="stash-product-card__pricing">
              {calculator?.canSellByCase && (
              <div className="text-center">
                <div className="stash-product-card__price-value">
                  {hasDiscount && (
                    <span className="stash-product-card__price-original me-1">
                      {getOriginalCasePriceDisplay()}
                    </span>
                  )}
                  <span className={`text-stash-accent ${hasDiscount ? 'stash-product-card__price-discount' : ''}`}>
                    {getCasePriceDisplay()}
                  </span>
                  <span className="text-stash-gray-600">/{calculator?.caseLabel || 'Case'}</span>
                </div>
                <div className="stash-product-card__fee-note">
                  ({product.unitsPerCase} {calculator?.unitLabelPlural || 'Units'}/{calculator?.caseLabel || 'Case'})
                </div>
              </div>
              )}

              {calculator?.canSellByUnit && (
                <div className="text-center">
                  <div className="stash-product-card__price-value">
                    {hasDiscount && (
                      <span className="stash-product-card__price-original me-1">
                        {getOriginalUnitPriceDisplay()}
                      </span>
                    )}
                    <span className={`text-stash-accent ${hasDiscount ? 'stash-product-card__price-discount' : ''}`}>
                      {getUnitPriceDisplay()}
                    </span>
                    <span className="text-stash-gray-600">/{calculator?.unitLabel || 'Unit'}</span>
                  </div>
                  {product.singleUnitFee > 0 && (
                    <div className="stash-product-card__fee-note">
                      (+{formatCurrency(product.singleUnitFee)}/{calculator?.unitLabel || 'Unit'} fee)
                    </div>
                  )}
                </div>
              )}

              {/* Volume pricing badge */}
              {hasMOQPricing && (
                <div className="stash-product-card__volume-pricing text-center">
                  <PricingBadge product={product} />
                </div>
              )}
            </div>

            {/* Category */}
            {product.categoryName && (
              <></>
              // <div className="stash-product-card__category">
              //   <small>{product.categoryName}</small>
              // </div>
            )}
            {/* Quantity Selector — cart cases/units (back order edits happen in the modal) */}
            {effectiveShowAddToCart && showQuantitySelector && product.isInStock && (
              <div className="stash-product-card__quantity-selector">
                <div className="text-center">
                  {calculator?.canSellByCase && (
                    <>
                      <label className="form-label small fw-medium">
                        {calculator?.caseLabelPlural || 'Cases'}
                        {caseQuantity > 0 && (
                          <span className="text-stash-primary ms-1">
                            ({caseQuantity} in cart)
                          </span>
                        )}
                      </label>
                      <QuantityInput
                        value={caseQuantity}
                        className="mx-auto"
                        onChange={handleCaseChange}
                        quantityType="cases"
                        product={product}
                        currentUnitQuantity={singleUnitQuantity}
                        size="sm"
                        disabled={isUpdatingProductQuantity}
                        isUpdating={isUpdatingProductQuantity}
                      />
                    </>
                  )}
                  {calculator?.canSellByUnit && product.unitsPerCase > 1 && (
                    <>
                      <label className="form-label small fw-medium">
                        {calculator?.unitLabelPlural || 'Units'}
                        {singleUnitQuantity > 0 && (
                          <span className="text-stash-primary ms-1">
                            ({singleUnitQuantity} in cart)
                          </span>
                        )}
                      </label>
                      <QuantityInput
                        value={singleUnitQuantity}
                        className="mx-auto"
                        onChange={handleUnitChange}
                        quantityType="units"
                        product={product}
                        currentCaseQuantity={caseQuantity}
                        size="sm"
                        disabled={isUpdatingProductQuantity}
                        isUpdating={isUpdatingProductQuantity}
                      />
                    </>
                  )}
                </div>

                {/* Total preview */}
                {priceInfo && (
                  <div className="stash-product-card__total-preview">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-medium">Total:</span>
                      <span className="fw-bold text-stash-primary fs-5">
                        {formatCurrency(priceInfo.grandTotal)}
                      </span>
                    </div>
                    {priceInfo.hasSavings && (
                      <div className="text-success small mt-1">
                        <i className="bi bi-tag-fill me-1"></i>
                        Saving {priceInfo.savingsPercent}%
                        {priceInfo.isMOQPrice && " (Volume)"}
                      </div>
                    )}
                    {!validation.isValid && (
                      <div className="text-warning-emphasis bg-warning-subtle rounded mt-2 mb-0 py-1 px-2 text-center">
                        <small>
                          <i className="bi bi-info-circle me-1"></i>
                          {validation.error}
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="stash-product-card__actions">
              {canUseBackOrder && (canCreateBackOrder || currentBackOrderQty > 0) ? (
                /* Depleted OR a back order already exists: View Details + Adjust/Create Back Order
                   side by side, with the cart action below (in-stock only). The back-order button
                   stays visible whenever a back order exists, even after the cart is reduced. */
                <>
                  <div>
                    <button
                      className="btn btn-outline-stash-primary btn-sm w-100"
                      onClick={() => showProductModal(product.id)}
                    >
                      <i className="bi bi-eye-fill me-1"></i>
                      View Details
                    </button>
                    <button
                      className="btn btn-outline-dark btn-sm w-100 mt-2"
                      onClick={() => setShowBackOrderModal(true)}
                    >
                      <i className="bi bi-clock-history me-1"></i>
                      {currentBackOrderQty > 0 ? "View Back Order" : "Create Back Order"}
                    </button>
                  </div>
                  {product.isInStock && (
                    <button
                      className="btn btn-stash-primary btn-sm w-100"
                      onClick={handleQuickAdd}
                    >
                      <i
                        className={`bi ${currentCartQuantity > 0 ? "bi-cart-check" : "bi-cart-plus"} me-1`}
                      ></i>
                      {currentCartQuantity > 0 ? "Adjust Cart" : "Add to Cart"}
                    </button>
                  )}
                </>
              ) : (
                /* Normal: View Details on top, cart action below */
                <>
                  <button
                    className="btn btn-outline-stash-primary btn-sm stash-product-card__view-btn"
                    onClick={() => showProductModal(product.id)}
                  >
                    <i className="bi bi-eye-fill me-1"></i>
                    View Details
                  </button>
                  {effectiveShowAddToCart && (
                    <>
                      {!showQuantitySelector ? (
                        <div className="d-flex gap-1 flex-fill">
                          <button
                            className="btn btn-stash-primary btn-sm flex-fill"
                            disabled={!product.isInStock}
                            onClick={handleQuickAdd}
                          >
                            <i
                              className={`bi ${currentCartQuantity > 0 ? "bi-cart-check" : "bi-cart-plus"} me-1`}
                            ></i>
                            {currentCartQuantity > 0 ? "Adjust Cart" : "Add to Cart"}
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex gap-1 flex-fill">
                          <button
                            className="btn btn-stash-primary btn-sm flex-fill"
                            onClick={() => {
                              setShowQuantitySelector(false);
                            }}
                          >
                            <i className="bi bi-check2 me-1"></i>
                            Done
                          </button>
                          <button
                            className="btn btn-outline-stash-danger btn-sm"
                            onClick={() => {
                              // Reset to zero (clear from cart)
                              handleQuantityUpdate(0, 0);
                              setShowQuantitySelector(false);
                            }}
                            disabled={
                              isUpdatingProductQuantity || currentCartQuantity === 0
                            }
                            title="Remove from cart"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <BackOrderAdjustModal
          show={showBackOrderModal}
          onHide={() => setShowBackOrderModal(false)}
          product={product}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if this specific product's relevant data changed
    return (
      prevProps.product.id === nextProps.product.id &&
      prevProps.product.unitAvailable === nextProps.product.unitAvailable &&
      prevProps.product.unitPrice === nextProps.product.unitPrice &&
      prevProps.product.singleUnitFee === nextProps.product.singleUnitFee &&
      prevProps.showAddToCart === nextProps.showAddToCart
    );
  },
);

// Set display name for debugging
ProductCard.displayName = "ProductCard";

export default ProductCard;